import type { Voice } from '@listening/api-contract/legacy-client-types';
import { ComparisonTable } from '@/components/astro/new-design-25/SEO/comparison-table';
import { FeatureHighlight } from '@/components/astro/new-design-25/SEO/FeatureHighlight';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly';
import { SeoContent } from '@/components/astro/new-design-25/SEO/seo-content';
import { SolutionBenefits } from '@/components/astro/new-design-25/SEO/solution-benefits';
import { UseCases } from '@/components/astro/new-design-25/SEO/use-cases';
import { WebToAudioConverter } from '@/components/astro/new-design-25/SEO/web-to-audio-converter';
import { FAQ } from '@/components/astro/new-design-25/ui/FAQ';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { CategoryCard, toolCategories } from '@/routes/_marketing/tools';

const comparisonTableData = {
  title: 'Why Choose Listening for Web Articles',
  description:
    'Listening reads articles cleanly without the ads, popups, and navigation noise that break other read-aloud tools.',
  columns: [
    { header: 'Listening', isHighlighted: true },
    { header: 'Browser read-aloud' },
  ],
  rows: [
    {
      feature: 'Article Extraction',
      description: 'Reads only the article body, not ads or navigation.',
      values: ['Clean extraction', 'Reads everything on the page'],
    },
    {
      feature: 'Voice Quality',
      description: 'Natural AI voices vs system synthesizer.',
      values: ['8 natural AI voices', 'Built-in robotic voices'],
    },
    {
      feature: '24-Hour Read-Along',
      description:
        'Open the article in a temporary reader with audio controls.',
      values: ['Text view + playbar', 'Audio only'],
    },
    {
      feature: 'Cross-Device',
      description: 'Same audio on web, iOS, Android, and Chrome extension.',
      values: ['All platforms', 'Browser-only'],
    },
    {
      feature: 'Speed Control',
      description: 'Adjustable playback for skimming or careful review.',
      values: ['0.5x to 4x', 'Limited'],
    },
    {
      feature: 'Long-Form Friendly',
      description: 'Tuned for full articles, not just sentences.',
      values: ['Stays natural at length', 'Drifts over long pages'],
    },
  ],
};

const DEFAULT_FAQS = [
  {
    question: 'How does the web to audio converter work?',
    answer:
      'Paste any article URL. We fetch the page server-side, extract the main article body (skipping ads, navigation, and footers), and generate natural audio with the AI voice of your choice.',
  },
  {
    question: 'What kinds of pages work best?',
    answer:
      'Articles, blog posts, research papers, news stories — anything with a clear main body of text. Heavily JavaScript-rendered SPAs and paywalled content may not work; in those cases you can paste the text directly into the text-to-speech tool.',
  },
  {
    question: 'What are the free preview limits?',
    answer:
      'The free preview is capped at 25,000 characters per article. Sign up to convert full long-form articles with no caps.',
  },
  {
    question: 'Can I download the audio?',
    answer:
      'No. The free public tools now create a 24-hour Listening link instead of a downloadable file. The link opens the article in a read-along view with audio, speed controls, and the playbar.',
  },
  {
    question: 'Is anything stored on your servers?',
    answer:
      'We fetch and process the URL server-side to extract the article text, then generate audio. We do not store the article body itself — only the audio output, which is served from a temporary URL.',
  },
];

type Feature = { title: string; description: string };

type UseCaseEntry = {
  title: string;
  challenge: string;
  solutionSections: Array<{ label: string; content: string }>;
};

type SeoSection = {
  title: string;
  content: string;
  contentSecondary?: string;
};

type BenefitItem = { text: string; highlight?: string };

type FaqItem = { question: string; answer: string };

type Props = {
  converterHeading?: string;
  converterSubtitle?: string;
  preferredLang?: Voice['lang'];
  preferredGender?: Voice['gender'];
  featureBadge?: string;
  featureTitle?: string;
  featureSubtitle?: string;
  featureDescription?: string;
  featureCtaText?: string;
  featureVariant?: 'default' | 'gradient';
  featureFeatures?: Array<Feature>;
  solutionBenefitsData?: {
    title: string;
    items: Array<BenefitItem>;
  };
  useCasesData?: {
    title: string;
    description: string;
    useCases: Array<UseCaseEntry>;
  };
  seoSections?: Array<SeoSection>;
  faqTitle?: string;
  faqDescription?: string;
  faqs?: Array<FaqItem>;
};

export function WebToAudioDemoPage(props: Props) {
  const {
    converterHeading,
    converterSubtitle,
    preferredLang,
    preferredGender,
    featureBadge,
    featureTitle,
    featureSubtitle,
    featureDescription,
    featureCtaText,
    featureVariant,
    featureFeatures,
    solutionBenefitsData,
    useCasesData,
    seoSections,
    faqTitle = 'Web to audio FAQ',
    faqDescription = 'Quick answers about the free web to audio converter.',
    faqs = DEFAULT_FAQS,
  } = props;

  return (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>
      <LinksSrOnly />

      <div className='relative bg-white'>
        <NavBar />
        <main id='main-content' tabIndex={-1}>
          <WebToAudioConverter
            heading={converterHeading}
            preferredGender={preferredGender}
            preferredLang={preferredLang}
            subtitle={converterSubtitle}
          />
        </main>
      </div>

      {featureTitle && (
        <FeatureHighlight
          badge={featureBadge}
          ctaText={featureCtaText}
          description={featureDescription}
          features={featureFeatures}
          subtitle={featureSubtitle}
          title={featureTitle}
          variant={featureVariant}
        />
      )}

      {solutionBenefitsData && (
        <SolutionBenefits
          items={solutionBenefitsData.items}
          title={solutionBenefitsData.title}
        />
      )}

      {useCasesData && (
        <UseCases
          _useCases={useCasesData.useCases}
          description={useCasesData.description}
          title={useCasesData.title}
        />
      )}

      <div className='hidden sm:block'>
        <ComparisonTable
          columns={comparisonTableData.columns}
          description={comparisonTableData.description}
          rows={comparisonTableData.rows}
          title={comparisonTableData.title}
        />
      </div>

      {seoSections && <SeoContent sections={seoSections} />}

      <section
        aria-labelledby='web-more-tools-heading'
        className='bg-white px-6 py-16 text-[#0f0e0c] sm:px-8 lg:py-20'
      >
        <div className='mx-auto max-w-[1320px]'>
          <div className='mb-9 max-w-3xl'>
            <p className='font-mono text-[12px] leading-none font-medium tracking-[0.06em] text-[#8e887d] uppercase'>
              Use cases
            </p>
            <h2
              className='mt-3 text-[clamp(34px,4vw,56px)] leading-[1] font-bold tracking-[-0.035em] text-balance text-[#0f0e0c]'
              id='web-more-tools-heading'
            >
              Even more ways to use Listening
            </h2>
            <p className='mt-4 max-w-2xl text-[17px] leading-[1.55] text-[#5a564e]'>
              Explore focused landing pages for PDFs, articles, research papers,
              mobile read aloud, study workflows, and accessibility.
            </p>
          </div>
        </div>
        <div className='mx-auto grid max-w-[1320px] grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3'>
          {toolCategories.map((category) => (
            <CategoryCard category={category} key={category.id} />
          ))}
        </div>
      </section>

      <Testimonials />

      <div
        className='bg-linear-to-b from-azure-525/5 to-azure-525/20'
        role='complementary'
      >
        <FAQ
          description={faqDescription}
          faqs={faqs}
          showApplyButton={false}
          title={faqTitle}
        />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );
}
